import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useChampionship } from "@/context/ChampionshipContext";
import { imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, ExternalLink, FileText } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLink =
  "text-sm font-medium text-foreground/80 hover:text-foreground transition-colors";
const activeLink = "text-foreground";

export default function Header() {
  const { current, all, siteAssets, documents } = useChampionship() || {};
  const { user, logout } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const year = current?.year || "";
  const location = current?.location || "";

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-border/60"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <div className="relative w-8 h-8">
            <div className="absolute inset-0 rounded-full othello-disc-dark border border-black" />
            <div className="absolute top-0 left-0 w-4 h-4 rounded-full othello-disc shadow-sm border border-neutral-300" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold tracking-tight text-base">
              WOC {year}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {location || "World Championship"}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to="/" end className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-home">
            Home
          </NavLink>
          <NavLink to="/teams" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-teams">
            Teams
          </NavLink>
          <NavLink to="/news" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-news">
            News
          </NavLink>
          <NavLink to="/live" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-live">
            Live
          </NavLink>
          <DropdownMenu>
            <DropdownMenuTrigger className={`${navLink} inline-flex items-center gap-1 outline-none`} data-testid="nav-info">
              Information <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[230px]">
              <DropdownMenuItem onClick={() => navigate("/info/venue")} data-testid="nav-info-venue">Venue</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/info/schedule")} data-testid="nav-info-schedule">Schedule</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/info/about-helsinki")} data-testid="nav-info-about">About Helsinki</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/info/youth-woc")} data-testid="nav-info-youth">Youth WOC</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/info/prizes")} data-testid="nav-info-prizes">Prizes</DropdownMenuItem>
              {siteAssets?.rules_pdf_path && (
                <DropdownMenuItem
                  onClick={() => window.open(imageUrl(siteAssets.rules_pdf_path), "_blank", "noopener,noreferrer")}
                  data-testid="nav-info-rules"
                  className="flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5" />
                    Tournament Rules
                  </span>
                  <ExternalLink className="h-3 w-3 opacity-50" />
                </DropdownMenuItem>
              )}
              {(documents || []).map((doc) => (
                <DropdownMenuItem
                  key={doc.id}
                  onClick={() => window.open(imageUrl(doc.path), "_blank", "noopener,noreferrer")}
                  data-testid={`nav-info-doc-${doc.id}`}
                  className="flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5" />
                    {doc.name}
                  </span>
                  <ExternalLink className="h-3 w-3 opacity-50" />
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => window.open("https://www.worldothello.org/about/tournaments/world-othello-championship/woc", "_blank", "noopener,noreferrer")}
                data-testid="nav-info-history"
                className="flex items-center justify-between"
              >
                <span>WOC History</span>
                <ChevronDown className="h-3 w-3 -rotate-90 opacity-50" />
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open("https://www.worldothello.org/about/tournaments/world-othello-championship/woc-champions", "_blank", "noopener,noreferrer")}
                data-testid="nav-info-champions"
                className="flex items-center justify-between"
              >
                <span>Previous Champions</span>
                <ChevronDown className="h-3 w-3 -rotate-90 opacity-50" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink to="/partners" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-partners">
            Partners
          </NavLink>
          <NavLink to="/contact" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-contact">
            Contact
          </NavLink>
          {all?.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger className={`${navLink} inline-flex items-center gap-1`} data-testid="nav-archive">
                Archive <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {all.map((c) => (
                  <DropdownMenuItem key={c.id} data-testid={`nav-archive-${c.year}`}>
                    {c.year} — {c.location}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="header-admin-link">
              <Button variant="ghost" size="sm">Admin</Button>
            </Link>
          )}
          {user && user.role === "captain" && (
            <Link to="/captain" data-testid="header-captain-link">
              <Button variant="ghost" size="sm">My Team</Button>
            </Link>
          )}
          {user ? (
            <Button variant="outline" size="sm" onClick={logout} data-testid="header-logout">
              Sign out
            </Button>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" size="sm">Sign in</Button>
              </Link>
              <Link to="/register" data-testid="header-register-link">
                <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md">
                  Captain register
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            <Link to="/" onClick={() => setOpen(false)} data-testid="mobile-nav-home">Home</Link>
            <Link to="/teams" onClick={() => setOpen(false)} data-testid="mobile-nav-teams">Teams</Link>
            <Link to="/news" onClick={() => setOpen(false)} data-testid="mobile-nav-news">News</Link>
            <Link to="/live" onClick={() => setOpen(false)} data-testid="mobile-nav-live">Live</Link>
            <Link to="/info/venue" onClick={() => setOpen(false)} data-testid="mobile-nav-venue">Venue</Link>
            <Link to="/info/schedule" onClick={() => setOpen(false)} data-testid="mobile-nav-schedule">Schedule</Link>
            <Link to="/info/about-helsinki" onClick={() => setOpen(false)} data-testid="mobile-nav-about">About Helsinki</Link>
            <Link to="/info/youth-woc" onClick={() => setOpen(false)} data-testid="mobile-nav-youth">Youth WOC</Link>
            <Link to="/info/prizes" onClick={() => setOpen(false)} data-testid="mobile-nav-prizes">Prizes</Link>
            {siteAssets?.rules_pdf_path && (
              <a href={imageUrl(siteAssets.rules_pdf_path)} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>Tournament Rules ↗</a>
            )}
            {(documents || []).map((doc) => (
              <a key={doc.id} href={imageUrl(doc.path)} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>{doc.name} ↗</a>
            ))}
            <a href="https://www.worldothello.org/about/tournaments/world-othello-championship/woc" target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>WOC History ↗</a>
            <a href="https://www.worldothello.org/about/tournaments/world-othello-championship/woc-champions" target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>Previous Champions ↗</a>
            <Link to="/partners" onClick={() => setOpen(false)}>Partners</Link>
            <Link to="/contact" onClick={() => setOpen(false)}>Contact</Link>
            {user && user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)}>Admin</Link>}
            {user && user.role === "captain" && <Link to="/captain" onClick={() => setOpen(false)}>My Team</Link>}
            {!user ? (
              <>
                <Link to="/login" onClick={() => setOpen(false)}>Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)}>Captain register</Link>
              </>
            ) : (
              <button onClick={() => { logout(); setOpen(false); }} className="text-left">Sign out</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
